import { useQuery } from 'urql';
import flush from 'just-flush';
import { useMemo } from 'react';
import type { ICallToAction } from '@stereoagency/ui';
import { FindCallToActionsByIdQueryDocument } from '@/gql/client/graphql';
import { useFragment } from '@/gql';
import { CallToActionFieldsFragment } from '@/gql/documents/fragments';
import { acfFirst } from '@/utils';

export function useCallToActions<T extends boolean>(ids: number[], asMap: T) {
  type ReturnValue = T extends true ? Map<number, ICallToAction> : ICallToAction[];

  const [{ data: callToActionsQuery }] = useQuery({
    query: FindCallToActionsByIdQueryDocument,
    pause: ids.length === 0,
    variables: { ids: useMemo(() => ids.map((id) => String(id)).sort(), [ids]) },
  });
  const callToActionsFields = useFragment(CallToActionFieldsFragment, flush(callToActionsQuery?.callToActions?.nodes ?? []));
  if (typeof window !== 'undefined') {
    console.info('callToActionsFields', callToActionsFields);
  }

  return useMemo<ReturnValue>(() => {
    const toCallToAction = (action: (typeof callToActionsFields)[number]): ICallToAction => {
      const fields = action.cptCallToActionCustomFields;
      const brandStyle = acfFirst(fields?.brandStyle);
      const buttonVariant = acfFirst(fields?.buttonVariant);
      return {
        label: fields?.label || action.title || '',
        href: fields?.href?.url ?? '/',
        target: fields?.href?.target ?? undefined,
        style: (acfFirst(fields?.style) ?? undefined) as ICallToAction['style'],
        ...(brandStyle
          ? { brandVariant: brandStyle === 'secondary' ? 'secondary' : 'primary' }
          : {}),
        ...(buttonVariant
          ? { buttonVariant: buttonVariant === 'secondary' ? 'secondary' : 'primary' }
          : {}),
      };
    };

    if (asMap) {
      return new Map(
        callToActionsFields.map((action) => [action.databaseId, toCallToAction(action)])
      ) as ReturnValue;
    }
    return callToActionsFields.map(toCallToAction) as ReturnValue;
  }, [callToActionsFields]);
}
